import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';

export type SubjectGradeItem = {
  id: number;
  value: number | null;
  qualifier: string | null;
  comment: string | null;
  date: string | null;
  created_at: string;
  is_thesis?: boolean;
  is_final?: boolean;
  is_lab?: boolean;
  is_seminar?: boolean;
  is_homework?: boolean;
  is_conduct?: boolean;
  category_name?: string | null;
  teacher_name?: string | null;
};

export type SubjectAbsenceItem = {
  id: number;
  justified: string | null;
  date?: string | null;
  created_at: string;
  justification_reason?: string | null;
};

interface Props {
  subjectName: string;
  teacherName: string | null;
  educationLevel: 'primary' | 'middle' | 'high' | 'university';
  grades: SubjectGradeItem[];
  absences: SubjectAbsenceItem[];
}

function gradeToNumber(value: number | null, qualifier: string | null, educationLevel: Props['educationLevel']): number | null {
  if (typeof value === 'number') return value;
  if (educationLevel !== 'primary') return null;
  const q = (qualifier || '').toString().trim().toUpperCase();
  if (!q) return null;
  if (q === 'FB') return 10;
  if (q === 'B') return 8;
  if (q === 'S') return 6;
  if (q === 'I') return 4;
  return null;
}

function avg(nums: number[]): number | null {
  if (!nums.length) return null;
  return Math.round((nums.reduce((s, v) => s + v, 0) / nums.length) * 100) / 100;
}

function typeLabel(g: SubjectGradeItem): string {
  if (g.is_conduct) return 'Purtare';
  if (g.is_thesis) return 'Teză';
  if (g.is_final) return 'Finală';
  if (g.is_lab) return 'Laborator';
  if (g.is_seminar) return 'Seminar';
  const cat = (g.category_name || '').toString().trim();
  if (cat) return cat;
  if (g.is_homework) return 'Temă';
  return 'Notă';
}

function gradeColor(value: number | string, educationLevel: Props['educationLevel']): string {
  if (educationLevel === 'primary') {
    const q = (value || '').toString().trim().toUpperCase();
    if (q === 'FB') return 'text-success';
    if (q === 'B') return 'text-primary';
    if (q === 'S') return 'text-warning';
    if (q === 'I') return 'text-error';
    return 'text-muted-foreground';
  }
  if (typeof value === 'number') {
    if (value >= 8) return 'text-success';
    if (value >= 6) return 'text-primary';
    if (value >= 4) return 'text-warning';
    return 'text-error';
  }
  return 'text-foreground';
}

function parseJustified(justified: string | null | undefined): { label: string; className: string } {
  const j = (justified || '').toString().trim().toLowerCase();
  if (j === 'da') return { label: 'Motivată', className: 'bg-success/10 text-success border-success/20' };
  if (j === 'nu') return { label: 'Nemotivată', className: 'bg-error/10 text-error border-error/20' };
  return { label: '—', className: 'bg-muted text-muted-foreground border-border' };
}

const SubjectGradesCard: React.FC<Props> = ({ subjectName, teacherName, educationLevel, grades, absences }) => {
  const [open, setOpen] = useState(false);

  const sortedGrades = useMemo(() => {
    return [...grades].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [grades]);

  const numericGrades = useMemo(() => {
    return sortedGrades
      .map((g) => gradeToNumber(g.value, g.qualifier, educationLevel))
      .filter((v): v is number => typeof v === 'number');
  }, [educationLevel, sortedGrades]);

  const average = useMemo(() => avg(numericGrades), [numericGrades]);
  const rating = useMemo(() => {
    if (average == null) return null;
    return Math.max(0, Math.min(100, Math.round((average / 10) * 100)));
  }, [average]);

  const trend = useMemo(() => {
    if (numericGrades.length < 2) return null;
    const recent = numericGrades.slice(0, 3);
    const prev = numericGrades.slice(3, 6);
    if (!prev.length) return null;
    const a = avg(recent);
    const b = avg(prev);
    if (a == null || b == null) return null;
    if (a > b) return { dir: 'up' as const, delta: Math.round((a - b) * 100) / 100 };
    if (a < b) return { dir: 'down' as const, delta: Math.round((b - a) * 100) / 100 };
    return { dir: 'flat' as const, delta: 0 };
  }, [numericGrades]);

  const absCounts = useMemo(() => {
    const total = absences.length;
    const motivated = absences.filter((a) => (a.justified || '').toString().toLowerCase() === 'da').length;
    return { total, motivated };
  }, [absences]);

  const headerRight = useMemo(() => {
    if (average == null) return '—';
    return String(average);
  }, [average]);

  const trendIcon = trend?.dir === 'up' ? 'TrendingUp' : trend?.dir === 'down' ? 'TrendingDown' : 'Minus';
  const trendClass =
    trend?.dir === 'up' ? 'text-success' : trend?.dir === 'down' ? 'text-error' : 'text-muted-foreground';

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-all duration-200">
      <button
        type="button"
        className="w-full text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
              <Icon name="BookOpen" size={20} className="text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground truncate">{subjectName}</div>
              <div className="text-xs text-muted-foreground truncate">{teacherName || '—'}</div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Icon name="Award" size={12} />
                  <span>Medie: {headerRight}</span>
                </div>
                {rating != null && (
                  <div className="flex items-center gap-1">
                    <Icon name="Percent" size={12} />
                    <span>Rating: {rating}%</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Icon name="Calendar" size={12} />
                  <span>Absențe: {absCounts.total}{absCounts.total ? ` (${absCounts.motivated} motivate)` : ''}</span>
                </div>
                {trend && (
                  <div className={`flex items-center gap-1 ${trendClass}`}>
                    <Icon name={trendIcon} size={12} />
                    <span>Evoluție: {trend.dir === 'flat' ? 'stabil' : trend.dir === 'up' ? 'în creștere' : 'în scădere'}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Note</div>
              <div className="text-sm font-semibold text-foreground">{grades.length}</div>
            </div>
            <Icon
              name="ChevronDown"
              size={18}
              className={`text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
      </button>

      {open && (
        <div className="mt-4 pt-4 border-t border-border space-y-4 animate-scale-in">
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-2">Note</div>
            {sortedGrades.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nu există note.</div>
            ) : (
              <div className="space-y-2">
                {sortedGrades.map((g) => {
                  const shown = g.value != null ? g.value : (g.qualifier || '—');
                  const label = typeLabel(g);
                  const when = g.date || g.created_at;
                  return (
                    <div key={g.id} className="border border-border rounded-lg p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-lg font-bold ${gradeColor(shown as any, educationLevel)}`}>{shown as any}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                              {label}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {new Date(when).toLocaleDateString('ro-RO', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </div>
                          {g.comment && (
                            <div className="text-sm text-foreground mt-2 whitespace-pre-line">
                              {g.comment}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs font-medium text-muted-foreground mb-2">Absențe</div>
            {absences.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nu există absențe.</div>
            ) : (
              <div className="space-y-2">
                {absences
                  .slice()
                  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  .map((a) => {
                    const tag = parseJustified(a.justified);
                    const when = a.date || a.created_at;
                    return (
                      <div key={a.id} className="border border-border rounded-lg p-3 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm text-foreground">
                            {new Date(when).toLocaleDateString('ro-RO', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </div>
                          {a.justification_reason && (
                            <div className="text-xs text-muted-foreground mt-1">{a.justification_reason}</div>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${tag.className}`}>{tag.label}</span>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectGradesCard;

